import React from 'react'
import { useApp } from '../../context/AppContext'
import type { SettingsPanel } from '../../types/app'
import { useDialog } from '../../hooks/useDialog'
import './Settings.css'
import { BackLink } from '../../ui/Icon'

// Settings is five pages grouped by the job (principle P4, design review):
// each page shows its panels as sections, in the order listed. A panel inside
// a group renders as a section instead of a page of its own (Panel reads this).
export type SettingsGroupId = 'business' | 'menu' | 'booking-page' | 'reviews' | 'payments'

export const GROUPS: { id: SettingsGroupId; title: string; detail: string; panels: SettingsPanel[] }[] = [
  // Hours first: time off is what she comes back for; the details are set once.
  { id: 'business', title: 'Your business', detail: 'Hours and time off, business details, calendar sync', panels: ['hours', 'business', 'calendar-feed'] },
  { id: 'menu', title: 'Menu', detail: 'Your treatments, packages, offers and consultation forms', panels: ['services', 'packages', 'offers', 'forms'] },
  { id: 'booking-page', title: 'Booking page', detail: 'Your link, how your page looks, and your booking rules', panels: ['online-booking'] },
  { id: 'reviews', title: 'Reviews', detail: 'Read and reply to what clients say', panels: ['reviews'] },
  { id: 'payments', title: 'Plan and payments', detail: 'Your plan, card payments and payouts', panels: ['plan', 'card-payments'] },
]

export function groupOf(panel: SettingsPanel) {
  return GROUPS.find((g) => g.panels.includes(panel)) ?? GROUPS[0]
}

type Embedding = { title: string; setUnsaved: (title: string, unsaved: boolean) => void }
export const GroupContext = React.createContext<Embedding | null>(null)

// How long a link to a section keeps it in view while the page loads.
const SETTLE_MS = 3000

// The id a section's element carries, so a link to one panel can scroll to it.
export const sectionId = (panel: SettingsPanel) => `settings-${panel}`

// The open group's sections with edits not yet saved, so leaving another way
// (a tab in the side rail) can ask first too.
let openUnsaved: Set<string> | null = null

// leaveSettingsOk asks before leaving a Settings page with unsaved edits.
export function leaveSettingsOk(): boolean {
  if (!openUnsaved || openUnsaved.size === 0) return true
  return window.confirm('Leave without saving? Your changes will be lost.')
}

export default function SettingsGroup({ panel, children }: { panel: SettingsPanel; children: React.ReactNode }) {
  const { dispatch } = useApp()
  const group = groupOf(panel)
  const unsaved = React.useRef(new Set<string>())
  React.useEffect(() => {
    openUnsaved = unsaved.current
    return () => {
      if (openUnsaved === unsaved.current) openUnsaved = null
    }
  }, [])
  const dialogRef = React.useRef<HTMLDivElement>(null)
  const embedding = React.useMemo<Embedding>(() => ({
    title: group.title,
    setUnsaved: (title, on) => { if (on) unsaved.current.add(title); else unsaved.current.delete(title) },
  }), [group.title])

  // Opened for one panel (a link from elsewhere): bring its section into view,
  // and hold it there while the sections above it load, until she scrolls.
  React.useEffect(() => {
    if (panel === group.panels[0]) return
    const section = document.getElementById(sectionId(panel))
    const sections = section?.parentElement
    if (!section || !sections) return
    const bring = () => section.scrollIntoView({ block: 'start' })
    bring()
    if (typeof ResizeObserver === 'undefined') return
    const loading = new ResizeObserver(bring)
    loading.observe(sections)
    const stop = () => loading.disconnect()
    const timer = window.setTimeout(stop, SETTLE_MS)
    const hers = ['wheel', 'touchstart', 'keydown', 'pointerdown']
    hers.forEach((type) => window.addEventListener(type, stop, { once: true, passive: true }))
    return () => {
      stop()
      window.clearTimeout(timer)
      hers.forEach((type) => window.removeEventListener(type, stop))
    }
  }, [panel, group])

  const back = () => {
    if (unsaved.current.size > 0 && !window.confirm('Leave without saving? Your changes will be lost.')) return
    dispatch({ type: 'CLOSE_SETTINGS_PANEL' })
  }
  useDialog(dialogRef, back)

  return (
    <div ref={dialogRef} className="settings-panel" role="dialog" aria-modal="true" aria-label={group.title}>
      <div className="menu-header">
        <BackLink onClick={back}>Settings</BackLink>
      </div>
      <div className="settings-body">
        <h1 className="menu-title">{group.title}</h1>
        <GroupContext.Provider value={embedding}>
          <div className="settings-sections">{children}</div>
        </GroupContext.Provider>
      </div>
    </div>
  )
}
